import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BidStrategyConfiguration = ({ onSaveStrategy, onTemplateApply }) => {
  const [activeTab, setActiveTab] = useState('rules');
  const [strategyName, setStrategyName] = useState('');
  const [bidRules, setBidRules] = useState([
    {
      id: 1,
      name: 'High Value Auto Insurance',
      condition: 'age >= 25 AND state IN ["CA", "TX", "FL"]',
      bidAmount: 45.50,
      maxBid: 65.00,
      enabled: true
    }
  ]);

  const stateOptions = [
    { value: 'CA', label: 'California' },
    { value: 'TX', label: 'Texas' },
    { value: 'FL', label: 'Florida' },
    { value: 'NY', label: 'New York' },
    { value: 'IL', label: 'Illinois' }
  ];

  const trafficSourceOptions = [
    { value: 'social', label: 'Social Media' },
    { value: 'search', label: 'Search Engine' },
    { value: 'tv', label: 'Television' },
    { value: 'radio', label: 'Radio' },
    { value: 'direct_mail', label: 'Direct Mail' }
  ];

  const addNewRule = () => {
    const newRule = {
      id: Date.now(),
      name: `New Rule ${bidRules?.length + 1}`,
      condition: '',
      bidAmount: 25.00,
      maxBid: 50.00,
      enabled: true
    };
    setBidRules([...bidRules, newRule]);
  };

  const updateRule = (id, field, value) => {
    setBidRules(prev => prev?.map(rule => 
      rule?.id === id ? { ...rule, [field]: value } : rule
    ));
  };

  const deleteRule = (id) => {
    setBidRules(prev => prev?.filter(rule => rule?.id !== id));
  };

  const handleSave = () => {
    const strategy = {
      name: strategyName,
      rules: bidRules,
      timestamp: new Date()
    };
    onSaveStrategy(strategy);
  };

  return (
    <div className="h-full flex flex-col bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Settings" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Bid Strategy Configuration</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={() => onTemplateApply('template1')}
          >
            Load Template
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Save"
            onClick={handleSave}
          >
            Save Strategy
          </Button>
        </div>
      </div>
      {/* Strategy Name */}
      <div className="p-4 border-b border-border">
        <Input
          label="Strategy Name"
          type="text"
          placeholder="Enter strategy name"
          value={strategyName}
          onChange={(e) => setStrategyName(e?.target?.value)}
          className="max-w-md"
        />
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {[
          { key: 'rules', label: 'Bid Rules', icon: 'Zap' },
          { key: 'demographics', label: 'Demographics', icon: 'Users' },
          { key: 'geographic', label: 'Geographic', icon: 'MapPin' },
          { key: 'schedule', label: 'Schedule', icon: 'Clock' }
        ]?.map((tab) => (
          <button
            key={tab?.key}
            onClick={() => setActiveTab(tab?.key)}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth ${
              activeTab === tab?.key
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'rules' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">Automated Bid Rules</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={addNewRule}
              >
                Add Rule
              </Button>
            </div>

            <div className="space-y-3">
              {bidRules?.map((rule) => (
                <div key={rule?.id} className="p-4 border border-border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between mb-3">
                    <Input
                      type="text"
                      placeholder="Rule name"
                      value={rule?.name}
                      onChange={(e) => updateRule(rule?.id, 'name', e?.target?.value)}
                      className="max-w-xs"
                    />
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        label="Enabled"
                        checked={rule?.enabled}
                        onChange={(e) => updateRule(rule?.id, 'enabled', e?.target?.checked)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => deleteRule(rule?.id)}
                        className="text-error hover:text-error"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Condition"
                      type="text"
                      placeholder="age >= 25 AND state = 'CA'"
                      value={rule?.condition}
                      onChange={(e) => updateRule(rule?.id, 'condition', e?.target?.value)}
                    />
                    <Input
                      label="Bid Amount ($)"
                      type="number"
                      step="0.25"
                      min="0"
                      value={rule?.bidAmount}
                      onChange={(e) => updateRule(rule?.id, 'bidAmount', parseFloat(e?.target?.value))}
                    />
                    <Input
                      label="Max Bid ($)"
                      type="number"
                      step="0.25"
                      min="0"
                      value={rule?.maxBid}
                      onChange={(e) => updateRule(rule?.id, 'maxBid', parseFloat(e?.target?.value))}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'demographics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-foreground">Age Range</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Min Age"
                    type="number"
                    min="18"
                    max="100"
                    defaultValue="25"
                  />
                  <Input
                    label="Max Age"
                    type="number"
                    min="18"
                    max="100"
                    defaultValue="65"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-foreground">Income Range</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Min Income ($)"
                    type="number"
                    min="0"
                    defaultValue="30000"
                  />
                  <Input
                    label="Max Income ($)"
                    type="number"
                    min="0"
                    defaultValue="150000"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">Traffic Sources</h3>
              <Select
                options={trafficSourceOptions}
                multiple
                searchable
                placeholder="Select traffic sources"
                value={[]}
                onChange={() => {}}
              />
            </div>
          </div>
        )}

        {activeTab === 'geographic' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">Target States</h3>
              <Select
                options={stateOptions}
                multiple
                searchable
                placeholder="Select target states"
                value={['CA', 'TX']}
                onChange={() => {}}
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">Zip Code Ranges</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Include Zip Codes"
                  type="text"
                  placeholder="90210, 10001-10099, 77*"
                  description="Comma-separated list, ranges, or wildcards"
                />
                <Input
                  label="Exclude Zip Codes"
                  type="text"
                  placeholder="90211, 10050"
                  description="Comma-separated list to exclude"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">Operating Hours</h3>
              <div className="grid grid-cols-7 gap-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']?.map((day) => (
                  <div key={day} className="space-y-2">
                    <div className="text-center">
                      <Checkbox label={day} checked={day !== 'Sun'} onChange={() => {}} />
                    </div>
                    <div className="space-y-1">
                      <Input
                        type="time"
                        defaultValue="09:00"
                        className="text-xs"
                      />
                      <Input
                        type="time"
                        defaultValue="17:00"
                        className="text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-foreground">Time Zone</h3>
              <Select
                options={[
                  { value: 'PST', label: 'Pacific Standard Time (PST)' },
                  { value: 'MST', label: 'Mountain Standard Time (MST)' },
                  { value: 'CST', label: 'Central Standard Time (CST)' },
                  { value: 'EST', label: 'Eastern Standard Time (EST)' }
                ]}
                value="PST"
                onChange={() => {}}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BidStrategyConfiguration;